import { useState } from "react";
import { NavLink } from "react-router-dom";
import CourseCard from "../components/CourseCard";
import SectionHeader from "../components/SectionHeader";
import TestimonialCard from "../components/TestimonialCard";
import { routePaths } from "../constants/routes";
import "../style/style.css";

const learningPaths = [
  {
    id: 1,
    title: "Beginner QA Path",
    description: "Start with SDLC, STLC, test cases, bug reports, Agile basics, and QA interview foundations.",
    duration: "3 to 4 weeks",
    cta: "Start Beginner Path",
    to: routePaths.manualTesting,
  },
  {
    id: 2,
    title: "Automation QA Path",
    description: "Practice locators, forms, waits, alerts, frames, ecommerce flows, API edge cases, and framework-style thinking.",
    duration: "6 to 8 weeks",
    cta: "Open Automation Lab",
    to: routePaths.automationTesting,
  },
  {
    id: 3,
    title: "API Testing Path",
    description: "Learn HTTP methods, status codes, request validation, auth, Postman, and API test strategy.",
    duration: "3 to 5 weeks",
    cta: "Learn API Testing",
    to: routePaths.apiTesting,
  },
];

const topCourses = [
  {
    title: "Complete QA Career Program",
    description: "Manual testing, Selenium, Playwright, API testing, SQL, projects, resume help, and mock interviews.",
    badge: "Most complete",
    duration: "4 to 5 months",
    price: "Premium live course",
    outcomes: ["Manual + Automation", "API + SQL", "Projects + interview prep"],
    to: routePaths.enroll,
  },
  {
    title: "Automation Testing Track",
    description: "Hands-on QA exercises with interactive components, scenario packs, ecommerce journeys, and Selenium or Playwright-style practice.",
    badge: "Practice-first",
    duration: "6 to 8 weeks",
    price: "Free + premium support",
    outcomes: ["Locators", "Forms and waits", "Real-time ecommerce flows"],
    to: routePaths.automationTesting,
  },
  {
    title: "API Testing Learning Path",
    description: "Build strong REST API fundamentals with testing strategy, status codes, and request validation.",
    badge: "Backend QA",
    duration: "3 to 5 weeks",
    price: "Course guidance available",
    outcomes: ["Postman basics", "Status validation", "API interview prep"],
    to: routePaths.apiTesting,
  },
  {
    title: "Manual Testing Foundation",
    description: "Learn test cases, test scenarios, bug reports, SDLC, STLC, Jira basics, and QA documentation.",
    badge: "Beginner friendly",
    duration: "3 to 4 weeks",
    price: "Foundation course",
    outcomes: ["Test case writing", "Bug reporting", "Interview basics"],
    to: routePaths.enroll,
  },
];

const latestPosts = [
  {
    title: "Software Testing Complete Guide",
    description: "A complete starting point for learners who want one structured place to begin.",
    type: "Latest blog",
    to: routePaths.softwareTesting,
  },
  {
    title: "Top Testing Interview Questions",
    description: "Practice interview answers for manual testing, automation testing, and QA fundamentals.",
    type: "Interview prep",
    to: routePaths.interviewQuestions,
  },
  {
    title: "Study Notes for Fast Revision",
    description: "Short notes and revision points for learners preparing for interviews or course practice.",
    type: "Study notes",
    to: routePaths.studyNotes,
  },
];

const whyQACoder = [
  {
    title: "Practical examples",
    description: "Concepts are connected to real QA work, test scenarios, and project-style examples.",
  },
  {
    title: "Interview-focused learning",
    description: "Guides, notes, and questions are built around what learners need to explain in interviews.",
  },
  {
    title: "Live automation practice",
    description: "The practice area gives learners browser components to inspect, automate, and test.",
  },
  {
    title: "Beginner friendly",
    description: "Start from manual testing basics and move step by step into automation and API testing.",
  },
];

const freeTools = [
  {
    title: "Automation Practice Lab",
    description: "Interactive QA widgets, ecommerce flows, testcase packs, and automation problem statements for hands-on practice.",
    to: routePaths.automationTesting,
  },
  {
    title: "Automation Testcase Library",
    description: "50+ scenario-driven automation testcases with execution steps, assertions, and framework ideas.",
    to: routePaths.automationTestcases,
  },
  {
    title: "Product Catalog Demo",
    description: "Use storefront, search, cart, address, payment, invoice, success, and tracking flows in one ecommerce practice area.",
    to: routePaths.product,
  },
  {
    title: "Premium PDFs / eBooks",
    description: "Browse low-ticket paid packs for JIRA, API testing, manual testing, interview prep, and project documents.",
    to: routePaths.premiumNotes,
  },
  {
    title: "API Cheat Sheet",
    description: "Quick-reference support for status codes, requests, responses, and API interview prep.",
    to: routePaths.apiCheatSheet,
  },
  {
    title: "Study Notes",
    description: "Use short revision notes to reinforce concepts quickly.",
    to: routePaths.studyNotes,
  },
];

const featuredShortcuts = [
  {
    title: "Automation Practice Lab",
    description: "Interactive components and real QA automation exercises.",
    to: routePaths.automationTesting,
  },
  {
    title: "Automation Testcase Library",
    description: "Scenario bank with 60 automation flows and execution steps.",
    to: routePaths.automationTestcases,
  },
  {
    title: "Premium PDFs / eBooks",
    description: "Paid notes, interview packs, and JIRA-style premium study material.",
    to: routePaths.premiumNotes,
  },
];

const testimonials = [
  {
    name: "Rahul Sharma",
    role: "QA Learner",
    rating: "5",
    result: "Fresher to QA interview-ready",
    quote:
      "The site made testing feel structured instead of overwhelming. I could learn, practice, and revise from one place.",
  },
  {
    name: "Priya Verma",
    role: "Automation Aspirant",
    rating: "5",
    result: "Manual testing to automation path",
    quote:
      "The practice area is what helped most. It feels closer to real test automation scenarios than just reading theory.",
  },
  {
    name: "Sachin Patel",
    role: "Manual to Automation Transition",
    rating: "4.8",
    result: "Improved interview confidence",
    quote:
      "I used the guides, interview questions, and notes together. That combination improved my confidence quickly.",
  },
];

const faqItems = [
  {
    question: "Who is QA Coder for?",
    answer:
      "QA Coder is for beginners, manual testers, automation learners, and job seekers who want structured software testing learning.",
  },
  {
    question: "Can I start learning for free before joining a course?",
    answer:
      "Yes. The website includes free guides, study notes, interview content, and interactive automation practice components.",
  },
  {
    question: "What makes this different from a normal content website?",
    answer:
      "This platform combines learning pages, hands-on practice, course guidance, and user account flows, so it feels closer to a product-based learning system.",
  },
  {
    question: "Do I need coding knowledge to begin?",
    answer:
      "No. You can start with manual testing basics, then move step by step into automation and API testing as your confidence grows.",
  },
];

export default function Home() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="home-page">
      <header className="home-hero">
        <div className="hero-copy">
          <span className="hero-chip">QA Coder Practical Learning Platform</span>
          <h1>Learn Software Testing the Practical Way</h1>
          <p>
            Build job-ready confidence with Manual Testing, Automation Testing,
            API Testing, live practice modules, and interview preparation in one
            structured learning platform.
          </p>

          <div className="hero-actions">
            <NavLink to={routePaths.automationTesting} className="primary-btn">
              Start Learning Free
            </NavLink>
            <NavLink to={routePaths.enroll} className="secondary-btn">
              Join Live Course
            </NavLink>
          </div>

          <div className="hero-highlights">
            <span>Manual + Automation + API</span>
            <span>Practice-first learning</span>
            <span>Interview-ready support</span>
          </div>
        </div>

        <div className="hero-product-panel">
          <div className="hero-panel-card hero-panel-primary">
            <div className="panel-label">Learning Sprint</div>
            <h3>Roadmap + practice + revision in one product flow.</h3>
            <div className="panel-list">
              <div>
                <strong>Manual QA</strong>
                <span>Manual testing foundation and test case thinking</span>
              </div>
              <div>
                <strong>Automation QA</strong>
                <span>Automation practice, locators, UI actions, and framework basics</span>
              </div>
              <div>
                <strong>API + Interview</strong>
                <span>API testing, interview preparation, and project readiness</span>
              </div>
            </div>
          </div>

          <div className="hero-panel-card hero-panel-secondary">
            <div className="mini-metrics">
              <div>
                <strong>300+</strong>
                <span>Learning modules</span>
              </div>
              <div>
                <strong>28</strong>
                <span>Hands-on practice topics</span>
              </div>
              <div>
                <strong>12</strong>
                <span>Interview sets</span>
              </div>
              <div>
                <strong>Live</strong>
                <span>Course enquiry + support flow</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <nav className="home-brand-links" aria-label="QA Coder main learning links">
        <NavLink to={routePaths.manualTesting}>Manual Testing</NavLink>
        <NavLink to={routePaths.automationTestingTopic}>Automation Testing</NavLink>
        <NavLink to={routePaths.apiTesting}>API Testing</NavLink>
        <NavLink to={routePaths.premiumNotes}>Premium PDFs</NavLink>
        <NavLink to={routePaths.product}>Product Catalog</NavLink>
        <NavLink to={routePaths.interviewQuestions}>Interview Questions</NavLink>
        <NavLink to={routePaths.enroll}>Enroll</NavLink>
        <NavLink to={routePaths.contact}>Contact</NavLink>
      </nav>

      <section className="trust-strip">
        <div className="trust-item">
          <strong>500+</strong>
          <span>Students Trained</span>
        </div>
        <div className="trust-item">
          <strong>250+</strong>
          <span>Interview Questions</span>
        </div>
        <div className="trust-item">
          <strong>28+</strong>
          <span>Practice Modules</span>
        </div>
        <div className="trust-item">
          <strong>4+</strong>
          <span>Courses Available</span>
        </div>
      </section>

      <section className="homepage-section section-roadmap">
        <SectionHeader
          eyebrow="Learning Paths"
          title="Choose the QA path that matches your current goal."
          description="Each path gives you a clear direction, estimated time, and a next action so you never feel stuck."
        />

        <div className="roadmap-grid">
          {learningPaths.map((step) => (
            <article key={step.id} className="roadmap-card">
              <div className="roadmap-step">{step.duration}</div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
              <NavLink to={step.to}> {step.cta} </NavLink>
            </article>
          ))}
        </div>
      </section>

      <section className="homepage-section section-courses">
        <SectionHeader
          eyebrow="Featured Courses"
          title="Top courses for beginners, career switchers, and automation learners."
          description="Start with a focused course or join the complete QA path when you want guided training."
        />

        <div className="content-card-grid">
          {topCourses.map((course) => (
            <CourseCard
              key={course.title}
              title={course.title}
              description={course.description}
              badge={course.badge}
              duration={course.duration}
              price={course.price}
              outcomes={course.outcomes}
              ctaLabel="View Course"
              ctaTo={course.to}
            />
          ))}
        </div>
      </section>

      <section className="homepage-section section-why">
        <SectionHeader
          eyebrow="Why QA Coder"
          title="A learning platform built around practical QA outcomes."
          description="QA Coder is not just a list of articles. It connects learning, practice, revision, and course guidance."
        />

        <div className="content-card-grid">
          {whyQACoder.map((item) => (
            <article key={item.title} className="content-card why-card">
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="homepage-section section-tools">
        <SectionHeader
          eyebrow="Free Tools and Practice"
          title="Practice first, then decide if you want premium guidance."
          description="Use the free tools to build confidence before joining live course support."
        />

        <div className="tool-grid">
          {freeTools.map((tool) => (
            <article key={tool.title} className="tool-card">
              <h3>{tool.title}</h3>
              <p>{tool.description}</p>
              <NavLink to={tool.to}>Open</NavLink>
            </article>
          ))}
        </div>
      </section>

      <section className="homepage-section section-tools">
        <SectionHeader
          eyebrow="Quick Access"
          title="Start from the most important pages directly."
          description="These are the main entry points if you want practice, testcase ideas, or premium notes right away."
        />

        <div className="tool-grid">
          {featuredShortcuts.map((item) => (
            <article key={item.title} className="tool-card">
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <NavLink to={item.to}>Open Now</NavLink>
            </article>
          ))}
        </div>
      </section>

      <section className="homepage-section section-why">
        <SectionHeader
          eyebrow="Featured Demo"
          title="Explore the catalog demo from the top level."
          description="This flow is built for product discovery, category navigation, and product-detail validation."
        />

        <div className="sticky-cta-card storefront-highlight-card">
          <div>
            <span className="section-kicker">Catalog Flow</span>
            <h2>One place to test storefront browsing, search, and product detail behavior.</h2>
            <p>
              Use the catalog demo to practice category browsing, flash sales, product discovery,
              search listing, add to cart, address, payment, invoice, order history, and tracking.
            </p>
          </div>
          <div className="sticky-cta-actions">
            <NavLink to={routePaths.product} className="primary-btn">
              Open Storefront
            </NavLink>
            <NavLink to={routePaths.flashSales} className="secondary-btn dark-secondary">
              View Flash Sales
            </NavLink>
          </div>
        </div>
      </section>

      <section className="homepage-section section-testimonials">
        <SectionHeader
          eyebrow="Student Success"
          title="Learners trust QA Coder because it turns confusion into a practical path."
        />

        <div className="testimonial-grid">
          {testimonials.map((item) => (
            <TestimonialCard key={item.name} {...item} />
          ))}
        </div>
      </section>

      <section className="homepage-section section-posts">
        <SectionHeader
          eyebrow="Latest Content"
          title="Latest blogs, interview questions, and study notes in one place."
          description="Move faster through theory, revision, and interview preparation with curated content."
        />

        <div className="content-card-grid">
          {latestPosts.map((post) => (
            <article key={post.title} className="content-card">
              <span className="card-tag">{post.type}</span>
              <h3>{post.title}</h3>
              <p>{post.description}</p>
              <NavLink to={post.to}>Read Now</NavLink>
            </article>
          ))}
        </div>
      </section>

      <section className="homepage-section sticky-cta-section">
        <div className="sticky-cta-card">
          <div>
            <span className="section-kicker">Ready to become a QA Engineer?</span>
            <h2>Start free today, or enroll when you want structured premium support.</h2>
            <p>
              Use the free learning hub if you are exploring, or join the premium course
              if you want guided training, mock interviews, and a faster QA career path.
            </p>
          </div>
          <div className="sticky-cta-actions">
            <NavLink to={routePaths.automationTesting} className="primary-btn">
              Start Learning Free
            </NavLink>
            <NavLink to={routePaths.enroll} className="secondary-btn dark-secondary">
              Enroll Now
            </NavLink>
          </div>
        </div>
      </section>

      <section className="homepage-section faq-section">
        <SectionHeader
          eyebrow="FAQ"
          title="Questions users usually ask before they join or begin."
        />

        <div className="faq-list">
          {faqItems.map((item, index) => (
            <article
              key={item.question}
              className={`faq-item ${openFaq === index ? "open" : ""}`}
            >
              <button type="button" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                <span>{item.question}</span>
                <span>{openFaq === index ? "-" : "+"}</span>
              </button>
              {openFaq === index && <p>{item.answer}</p>}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
